// ─── QuickAddDetails ──────────────────────────────────────────────────────────
// Per-listing "Quick Add Details" shown in the Generate view. Toggles reveal
// inline inputs. The Pickup Location supports persistent memory (localStorage),
// managed by the parent (App) — this component just reflects `value` and reports
// changes via `onChange`.
export default function QuickAddDetails({ value, onChange, hasSavedLocation = false }) {
  const { dimensions, price, location } = value;

  function updateSection(section, patch) {
    onChange({ ...value, [section]: { ...value[section], ...patch } });
  }

  return (
    <div className="qa">
      <div className="qa-heading">
        <h2 className="qa-title">Quick Add Details</h2>
        <p className="qa-sub">Toggle on anything you want appended to this listing.</p>
      </div>

      {/* ── Dimensions / Size ── */}
      <div className={`qa-card ${dimensions.enabled ? 'qa-card--open' : ''}`}>
        <SwitchRow
          label="Add Dimensions / Size?"
          checked={dimensions.enabled}
          onChange={(v) => updateSection('dimensions', { enabled: v })}
        />
        {dimensions.enabled && (
          <div className="qa-body">
            <div className="qa-seg" role="tablist" aria-label="Dimensions or Size">
              {[
                { key: 'dimensions', label: 'Dimensions' },
                { key: 'size', label: 'Size' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={(dimensions.mode || 'dimensions') === key}
                  className={`qa-seg-btn ${(dimensions.mode || 'dimensions') === key ? 'qa-seg-btn--on' : ''}`}
                  onClick={() => updateSection('dimensions', { mode: key })}
                >
                  {label}
                </button>
              ))}
            </div>

            {(dimensions.mode || 'dimensions') === 'dimensions' ? (
              <div className={`qa-dims ${dimensions.leaveBlank ? 'qa-disabled' : ''}`}>
                {[
                  { key: 'l', ph: 'L' },
                  { key: 'w', ph: 'W' },
                  { key: 'h', ph: 'H' },
                ].map(({ key, ph }, i) => (
                  <div key={key} className="qa-dim-wrap">
                    <input
                      className="qa-input qa-input--dim"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      placeholder={ph}
                      value={dimensions[key]}
                      disabled={dimensions.leaveBlank}
                      onChange={(e) => updateSection('dimensions', { [key]: e.target.value })}
                    />
                    {i < 2 && <span className="qa-x">×</span>}
                  </div>
                ))}
                <span className="qa-unit">cm</span>
              </div>
            ) : (
              <div className={`qa-size ${dimensions.leaveBlank ? 'qa-disabled' : ''}`}>
                <div className="qa-size-chips">
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`qa-chip ${dimensions.size === s ? 'qa-chip--on' : ''}`}
                      disabled={dimensions.leaveBlank}
                      onClick={() => updateSection('dimensions', { size: dimensions.size === s ? '' : s })}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <input
                  className="qa-input"
                  type="text"
                  placeholder={'Or type a size — e.g. 32", Size 10, Mens 10'}
                  value={dimensions.size}
                  disabled={dimensions.leaveBlank}
                  onChange={(e) => updateSection('dimensions', { size: e.target.value })}
                />
              </div>
            )}

            <CheckRow
              label="Leave blank for now"
              checked={dimensions.leaveBlank}
              onChange={(v) => updateSection('dimensions', { leaveBlank: v })}
            />
          </div>
        )}
      </div>

      {/* ── Price ── */}
      <div className={`qa-card ${price.enabled ? 'qa-card--open' : ''}`}>
        <SwitchRow
          label="Add Price?"
          checked={price.enabled}
          onChange={(v) => updateSection('price', { enabled: v })}
        />
        {price.enabled && (
          <div className="qa-body">
            <div className={`qa-money-field ${price.leaveBlank ? 'qa-disabled' : ''}`}>
              <span className="qa-money-prefix">$</span>
              <input
                className="qa-input qa-input--money"
                type="number"
                min="0"
                inputMode="decimal"
                placeholder="Price"
                value={price.value}
                disabled={price.leaveBlank}
                onChange={(e) => updateSection('price', { value: e.target.value })}
              />
            </div>

            <CheckRow
              label="Include RRP?"
              checked={price.includeRrp}
              onChange={(v) => updateSection('price', { includeRrp: v })}
            />

            {price.includeRrp && (
              <div className={`qa-money-field qa-money-field--rrp ${price.leaveBlank ? 'qa-disabled' : ''}`}>
                <span className="qa-money-prefix">$</span>
                <input
                  className="qa-input qa-input--money"
                  type="number"
                  min="0"
                  inputMode="decimal"
                  placeholder="RRP"
                  value={price.rrp}
                  disabled={price.leaveBlank}
                  onChange={(e) => updateSection('price', { rrp: e.target.value })}
                />
              </div>
            )}

            <CheckRow
              label="Leave blank for now"
              checked={price.leaveBlank}
              onChange={(v) => updateSection('price', { leaveBlank: v })}
            />
          </div>
        )}
      </div>

      {/* ── Pickup Location ── */}
      <div className={`qa-card ${location.enabled ? 'qa-card--open' : ''}`}>
        <SwitchRow
          label="Add Pickup Location?"
          checked={location.enabled}
          onChange={(v) => updateSection('location', { enabled: v })}
        />
        {location.enabled && (
          <div className="qa-body">
            {hasSavedLocation && !location.leaveBlank && (
              <p className="qa-memory-hint">📍 Loaded from your saved location — edit anytime.</p>
            )}
            <div className={`qa-loc ${location.leaveBlank ? 'qa-disabled' : ''}`}>
              <input
                className="qa-input"
                type="text"
                placeholder="Suburb"
                value={location.suburb}
                disabled={location.leaveBlank}
                onChange={(e) => updateSection('location', { suburb: e.target.value })}
              />
              <input
                className="qa-input qa-input--narrow"
                type="text"
                inputMode="numeric"
                placeholder="Postcode"
                maxLength={10}
                value={location.postcode}
                disabled={location.leaveBlank}
                onChange={(e) => updateSection('location', { postcode: e.target.value })}
              />
            </div>
            {!location.leaveBlank && (
              <CheckRow
                label="Save location to settings?"
                checked={location.save}
                onChange={(v) => updateSection('location', { save: v })}
              />
            )}
            <CheckRow
              label="Leave blank for now"
              checked={location.leaveBlank}
              onChange={(v) => updateSection('location', { leaveBlank: v })}
            />
          </div>
        )}
      </div>

      <style>{`
        .qa { margin-top: 20px; }
        .qa-heading { margin-bottom: 10px; }
        .qa-title {
          font-size: 11px; font-weight: 500; letter-spacing: 0.07em;
          text-transform: uppercase; color: var(--color-text-hint);
        }
        .qa-sub { font-size: 12px; color: var(--color-text-hint); margin-top: 4px; line-height: 1.5; }

        .qa-card {
          background: var(--color-bg-card);
          border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md);
          margin-bottom: 8px;
          overflow: hidden;
          transition: border-color 0.15s;
        }
        .qa-card--open { border-color: var(--color-primary-light); }

        .qa-body {
          padding: 4px 14px 14px;
          display: flex; flex-direction: column; gap: 10px;
          animation: qaReveal 0.18s ease;
        }
        @keyframes qaReveal { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: none; } }

        .qa-disabled { opacity: 0.4; pointer-events: none; }

        /* ── Segmented toggle (Dimensions | Size) ── */
        .qa-seg {
          display: flex; gap: 4px; padding: 3px;
          background: var(--color-bg-page); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md);
        }
        .qa-seg-btn {
          flex: 1; padding: 8px 6px; border: none; border-radius: calc(var(--radius-md) - 3px);
          background: transparent; color: var(--color-text-secondary);
          font-family: inherit; font-size: 13px; font-weight: 500;
          cursor: pointer; transition: background 0.15s, color 0.15s, box-shadow 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .qa-seg-btn--on {
          background: var(--color-bg-card); color: var(--color-text-heading);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }

        /* ── Size ── */
        .qa-size { display: flex; flex-direction: column; gap: 10px; }
        .qa-size-chips { display: flex; gap: 6px; }
        .qa-chip {
          flex: 1; padding: 8px 0; min-width: 0;
          background: var(--color-bg-input); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md); color: var(--color-text-secondary);
          font-family: inherit; font-size: 13px; font-weight: 500;
          cursor: pointer; transition: background 0.15s, color 0.15s, border-color 0.15s;
          -webkit-tap-highlight-color: transparent;
        }
        .qa-chip--on {
          background: var(--color-primary); border-color: var(--color-primary); color: #fff;
        }

        /* ── Dimensions ── */
        .qa-dims { display: flex; align-items: center; gap: 6px; }
        .qa-dim-wrap { display: flex; align-items: center; gap: 6px; flex: 1; }
        .qa-x { font-size: 13px; color: var(--color-text-hint); flex-shrink: 0; }
        .qa-unit { font-size: 12px; color: var(--color-text-hint); flex-shrink: 0; }
        .qa-input--dim { width: 100%; text-align: center; padding: 9px 6px; }

        /* ── Money field ── */
        .qa-money-field {
          display: flex; align-items: center;
          background: var(--color-bg-input); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md); overflow: hidden;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .qa-money-field:focus-within { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-focus-ring); }
        .qa-money-field--rrp { margin-left: 20px; }
        .qa-money-prefix { padding: 0 8px; font-size: 14px; color: var(--color-text-hint); flex-shrink: 0; }
        .qa-input--money {
          flex: 1; border: none !important; box-shadow: none !important;
          padding: 10px 12px 10px 0; border-radius: 0 !important; background: transparent;
        }
        .qa-input--money:focus { outline: none; }

        /* ── Location ── */
        .qa-loc { display: flex; gap: 8px; }
        .qa-loc .qa-input { flex: 1; }
        .qa-input--narrow { flex: 0 0 100px !important; }
        .qa-memory-hint {
          font-size: 12px; color: var(--color-primary);
          background: var(--color-primary-light);
          border-radius: var(--radius-sm); padding: 7px 10px; line-height: 1.4;
        }

        /* ── Shared input ── */
        .qa-input {
          width: 100%; padding: 10px 14px;
          background: var(--color-bg-input); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md); color: var(--color-text-heading);
          font-family: inherit; font-size: 16px; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s; -webkit-appearance: none;
        }
        .qa-input::placeholder { color: var(--color-text-hint); }
        .qa-input:focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-focus-ring); }
        .qa-input:disabled { background: var(--color-bg-page); }
        .qa-input[type=number]::-webkit-inner-spin-button,
        .qa-input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .qa-input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}

// ─── Switch row (toggle for each Quick Add section) ───────────────────────────
function SwitchRow({ label, checked, onChange }) {
  return (
    <div
      className="qa-switch-row"
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), onChange(!checked))}
    >
      <span className="qa-switch-label">{label}</span>
      <div className={`qa-switch ${checked ? 'qa-switch--on' : ''}`}>
        <div className="qa-switch-thumb" />
      </div>

      <style>{`
        .qa-switch-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 13px 14px; cursor: pointer; gap: 16px;
          -webkit-tap-highlight-color: transparent; outline: none;
        }
        .qa-switch-label { font-size: 15px; font-weight: 500; color: var(--color-text-heading); }
        .qa-switch {
          flex-shrink: 0; width: 46px; height: 27px; border-radius: 100px;
          background: var(--color-bg-page); border: 1px solid var(--color-border);
          position: relative; transition: background 0.2s, border-color 0.2s;
        }
        .qa-switch--on { background: var(--color-primary); border-color: var(--color-primary); }
        .qa-switch-thumb {
          position: absolute; top: 2px; left: 2px;
          width: 21px; height: 21px; border-radius: 50%; background: #fff;
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        }
        .qa-switch--on .qa-switch-thumb { transform: translateX(19px); }
        .qa-switch-row:focus-visible .qa-switch { box-shadow: 0 0 0 3px var(--color-focus-ring); }
      `}</style>
    </div>
  );
}

// ─── Checkbox row ─────────────────────────────────────────────────────────────
function CheckRow({ label, checked, onChange }) {
  return (
    <label className="qa-check-row">
      <span
        className={`qa-check ${checked ? 'qa-check--on' : ''}`}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={(e) => { e.preventDefault(); onChange(!checked); }}
        onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), onChange(!checked))}
      >
        {checked && (
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span className="qa-check-label" onClick={(e) => { e.preventDefault(); onChange(!checked); }}>{label}</span>

      <style>{`
        .qa-check-row { display: flex; align-items: center; gap: 9px; cursor: pointer; -webkit-tap-highlight-color: transparent; }
        .qa-check {
          flex-shrink: 0; width: 20px; height: 20px; border-radius: 6px;
          border: 1.5px solid var(--color-border-dashed); background: var(--color-bg-input);
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s, border-color 0.15s; outline: none;
        }
        .qa-check--on { background: var(--color-primary); border-color: var(--color-primary); }
        .qa-check svg { width: 13px; height: 13px; }
        .qa-check:focus-visible { box-shadow: 0 0 0 3px var(--color-focus-ring); }
        .qa-check-label { font-size: 13px; color: var(--color-text-secondary); cursor: pointer; }
      `}</style>
    </label>
  );
}
